import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import cn from "classnames";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import desktopIcon from "assets/icons/desc.svg";
import phoneIcon from "assets/icons/mob.svg";
import { useAppDispatch, useAppSelector } from "store";
import {
  CasinoBetsFilters,
  getPlayerCasinoBetsList,
  handleBetsPaginate,
} from "store/players/list/slice";
import { playersListLimits } from "configs/limits";

const CASINO_BETS_LIMIT = 10;

const initialFilters: CasinoBetsFilters = {
  createdFrom: "",
  createdTo: "",
  sessionId: "",
  transactionId: "",
  roundId: "",
  action: "",
  balanceType: "",
  gameTitle: "",
  gameProvider: "",
  amountMin: "",
  amountMax: "",
};

const displayValue = (value: unknown) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const CopyableId: React.FC<{
  value: string | null;
  maxVisibleCharacters?: number;
}> = ({ value, maxVisibleCharacters }) => {
  if (!value) return <>—</>;

  const visibleValue =
    maxVisibleCharacters && value.length > maxVisibleCharacters
      ? `${value.slice(0, maxVisibleCharacters)}…`
      : value;

  return (
    <div className="player-bets__copyable-id" title={value}>
      <CopyToClipboard text={value} />
      <span>{visibleValue}</span>
    </div>
  );
};

const CasinoDevice: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <>—</>;

  const [device = "", ...details] = value.split("|");
  const isDesktop = device.toLowerCase() === "desktop";
  const deviceDetails = details.filter(Boolean).join(" ") || device;

  return (
    <div className="user-devices" title={deviceDetails}>
      <img src={isDesktop ? desktopIcon : phoneIcon} alt={device} />
    </div>
  );
};

const ListCasinoTab: React.FC = () => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CasinoBetsFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<CasinoBetsFilters>
  >({});
  const [hasRequested, setHasRequested] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(playersListLimits[0]);

  const bets = useAppSelector((store) => store.players.casinoBetsList);
  const activePage = useAppSelector((store) => store.players.activePageBets);
  const totalItems = useAppSelector((store) => store.players.totalItemsBets);
  const totalPages = useAppSelector((store) => store.players.totalPagesBets);
  const isFetched = useAppSelector((store) => store.players.isBetsFetched);
  const error = useAppSelector((store) => store.players.betsError);

  const userId = searchParams.get("entityId");

  useEffect(() => {
    if (!userId) return;

    dispatch(
      getPlayerCasinoBetsList({
        id: Number(userId),
        limit: currentLimit,
        offset: activePage - 1,
        filters: appliedFilters,
      }),
    );
    setHasRequested(true);
  }, [activePage, appliedFilters, currentLimit, dispatch, userId]);

  const balanceTypeOptions = [
    {
      value: "real",
      label: t("real"),
    },
    {
      value: "bonus",
      label: t("bonus"),
    },
  ];

  const actionOptions = [
    {
      value: "bet",
      label: "bet",
    },
    {
      value: "win",
      label: "win",
    },
  ];

  const handleFilter = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setAppliedFilters({});
    dispatch(handleBetsPaginate(1));
  };

  const handleSearch = () => {
    const nextFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== ""),
    ) as Partial<CasinoBetsFilters>;

    setAppliedFilters(nextFilters);
    dispatch(handleBetsPaginate(1));
  };

  if (!hasRequested || !isFetched) {
    return (
        <div className="player-bets player-bets--casino">
        <ModalTabSkeleton />
      </div>
    );
  }

  return (
    <div className="player-bets player-bets--casino">
      <form
        className="player-bets__filters"
        onSubmit={(event) => {
          event.preventDefault();
          handleSearch();
        }}
      >
        <div className="player-bets__row player-bets__row--casino-primary">
          <div className="player-bets__item">
            <div className={cn("date-picker-wrapper")}>
              <div className="title">{t("startDate")}</div>

              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                dateFormat="dd.MM.yyyy"
                selected={
                  filters.createdFrom
                    ? moment(filters.createdFrom, "YYYY-MM-DD", true).toDate()
                    : null
                }
                onChange={(date) =>
                  handleFilter(
                    "createdFrom",
                    date ? moment(date).format("YYYY-MM-DD") : "",
                  )
                }
              />
            </div>
          </div>

          <div className="player-bets__item">
            <div className={cn("date-picker-wrapper")}>
              <div className="title">{t("endDate")}</div>

              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                dateFormat="dd.MM.yyyy"
                selected={
                  filters.createdTo
                    ? moment(filters.createdTo, "YYYY-MM-DD", true).toDate()
                    : null
                }
                onChange={(date) =>
                  handleFilter(
                    "createdTo",
                    date ? moment(date).format("YYYY-MM-DD") : "",
                  )
                }
              />
            </div>
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="session-id"
              type="text"
              label={t("sessionId")}
              placeholder={t("enterSessionId")}
              value={filters.sessionId}
              onChange={(value) => handleFilter("sessionId", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="transaction-id"
              type="text"
              label={t("transactionId")}
              placeholder={t("enterTransactionId")}
              value={filters.transactionId}
              onChange={(value) => handleFilter("transactionId", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="round-id"
              type="text"
              label={t("roundId")}
              placeholder={t("enterRoundId")}
              value={filters.roundId}
              onChange={(value) => handleFilter("roundId", value)}
            />
          </div>
        </div>

        <div className="player-bets__row player-bets__row--casino-secondary">
          <div className="player-bets__item">
            <DefaultDropdown
              label={t("action")}
              placeholder={t("all")}
              value={filters.action}
              options={actionOptions}
              onSelect={(value) => handleFilter("action", String(value))}
            />
          </div>

          <div className="player-bets__item">
            <DefaultDropdown
              label={t("balanceType")}
              placeholder={t("all")}
              value={filters.balanceType}
              options={balanceTypeOptions}
              onSelect={(value) => handleFilter("balanceType", String(value))}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="game-title"
              type="text"
              label={t("gameTitle")}
              placeholder={t("enterTitle")}
              value={filters.gameTitle}
              onChange={(value) => handleFilter("gameTitle", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="provider"
              type="text"
              label={t("provider")}
              placeholder={t("enterName")}
              value={filters.gameProvider}
              onChange={(value) => handleFilter("gameProvider", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="amount-min"
              type="number"
              label={t("amountMin")}
              placeholder={t("min")}
              value={filters.amountMin}
              onChange={(value) => handleFilter("amountMin", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="amount-max"
              type="number"
              label={t("amountMax")}
              placeholder={t("max")}
              value={filters.amountMax}
              onChange={(value) => handleFilter("amountMax", value)}
            />
          </div>

          <div className="player-bets__actions">
            <Button
              txt={t("reset")}
              isSecondary
              isSmall
              handleClick={handleReset}
            />
            <Button
              txt={t("search")}
              type="submit"
              isGreen
              isSmall
            />
          </div>
        </div>
      </form>

      <div className="inner-table-wrapper player-bets__table player-bets__table--casino">
        <table>
          <thead>
            <tr>
              <th>{t("id")}</th>
              <th>{t("date")}</th>
              <th>{t("action")}</th>
              <th>{t("amount")}</th>
              <th>{t("currency")}</th>
              <th>{t("balanceRealBonus")}</th>
              <th>{t("game")}</th>
              <th>{t("provider")}</th>
              <th>{t("transactionId")}</th>
              <th>{t("roundId")}</th>
              <th>{t("sessionId")}</th>
              <th>{t("device")}</th>
            </tr>
          </thead>

          <tbody>
            {isFetched && error && (
              <tr className="player-bets__empty-row player-bets__error-row">
                <td colSpan={12}>{t(error)}</td>
              </tr>
            )}

            {isFetched && !error && bets.length === 0 && (
              <tr className="player-bets__empty-row">
                <td colSpan={12}>{t("noBetsFound")}</td>
              </tr>
            )}

            {isFetched &&
              !error &&
              bets.map((bet) => (
                <tr key={bet.id}>
                  <td>{displayValue(bet.id)}</td>
                  <td className="date" title={displayValue(bet.created)}>
                    {bet.created ? (
                      <>
                        <span>{moment(bet.created).format("DD.MM.YYYY")}</span>
                        <span>{formatTime(bet.created, timeFormat, true)}</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={bet.action}>{displayValue(bet.action)}</td>
                  <td>{displayValue(bet.amount)}</td>
                  <td>{displayValue(bet.currency)}</td>
                  <td
                    title={`${displayValue(bet.balanceReal)} / ${displayValue(
                      bet.balanceBonus,
                    )}`}
                  >
                    {displayValue(bet.balanceReal)} /{" "}
                    {displayValue(bet.balanceBonus)}
                  </td>
                  <td title={displayValue(bet.gameTitle)}>
                    {displayValue(bet.gameTitle)}
                  </td>
                  <td title={displayValue(bet.gameProvider)}>
                    {displayValue(bet.gameProvider)}
                  </td>
                  <td>
                    <CopyableId
                      value={bet.transactionId}
                      maxVisibleCharacters={10}
                    />
                  </td>
                  <td>
                    <CopyableId value={bet.roundId} />
                  </td>
                  <td>
                    <CopyableId
                      value={bet.sessionId}
                      maxVisibleCharacters={10}
                    />
                  </td>
                  <td>
                    <CasinoDevice value={bet.userDevice} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {isFetched && !error && bets.length > 0 && (
        <Pagination
          activePage={activePage}
          limit={currentLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          limitsArray={playersListLimits}
          handleLimitClick={(limit) => {
            setCurrentLimit(limit);
            dispatch(handleBetsPaginate(1));
          }}
          handlePrevClick={() => {
            if (activePage > 1) {
              dispatch(handleBetsPaginate(activePage - 1));
            }
          }}
          handleNextClick={() => {
            if (activePage < totalPages) {
              dispatch(handleBetsPaginate(activePage + 1));
            }
          }}
        />
      )}
    </div>
  );
};

export default ListCasinoTab;
